import type { ResponsiveValue } from 'styled-system';
import { Alert, Box, Col, FormGroup, Radio, RadioGroup, TextInput } from '@ksyos/design-system';
import { useEffect } from 'react';
import { styled } from 'styled-components';
import type { ITfoDiagnosticReportCustomControlData } from '@common/validation/customControls/ophthalmology';
import type { FormResult } from '@frontend/utils/hooks/useForm';
import { tfoConvertOldToNewScreeningStandard } from '@common/customControls/tfoV1ConvertOldToNewScreeningStandard';
import {
    Maculopathie,
    Policies,
    Retinopathie,
    retinopathieOptions,
    retinopathieOptionsForMaculopathieM1,
} from '@common/validation/customControls/ophthalmology';
import { useFormContext } from '@frontend/utils/hooks/useFormContext';
import { AndereBevindingenFields } from './AndereBevindingenFields';
import { FundusscreeningPolicyAndUrgencyAddEditFields } from './FundusscreeningPolicyAndUrgencyAddEditFields';
import { NewStandardInfo } from './NewStandardInfo';

interface TfoV1DiagnosticReportAddEditCustomControlFieldsProps {
    customControlData: ITfoDiagnosticReportCustomControlData;
    isBiograder?: boolean;
    showAdvisedPolicy: boolean;
    showToelichting?: boolean;
}

const advisedPolicies = [
    Policies.controleVolgensRichtlijn,
    Policies.teleConsultOogarts,
    Policies.verwijzingOogartsDoorOptometrist,
    Policies.verwijzingOogartsDoorHuisarts,
    Policies.vervolgonderzoekOptometrist,
    Policies.anders,
];

const StyledRethinopathieMaculopathieItem = styled.div`
    display: inline-block;
    max-width: 19vw;
`;

const boxProps = { gap: 2 as ResponsiveValue<2>, style: { display: 'flex' } };
const alignLeftElement = { style: { marginLeft: 'auto', marginRight: 'auto' } };

export function TfoV1DiagnosticReportAddEditCustomControlFields(
    props: Readonly<TfoV1DiagnosticReportAddEditCustomControlFieldsProps>
) {
    const form = useFormContext<typeof props.customControlData>();

    useEffect(() => {
        if (hasToConvertOldToNewScreeningStandard(props.customControlData)) {
            form.handleChange([
                {
                    name: 'retinopathie_od',
                    value: tfoConvertOldToNewScreeningStandard(props.customControlData.bevindingen_npdr_od),
                },
                {
                    name: 'retinopathie_os',
                    value: tfoConvertOldToNewScreeningStandard(props.customControlData.bevindingen_npdr_os),
                },
            ]);
        }
    }, [form.values]);

    return (
        <>
            <RetinopathieAndMaculopathieFields />

            {props.showAdvisedPolicy && <PolicyAndUrgencyAddEditFields />}

            {props.showToelichting && (
                <FormGroup label="Toelichting">
                    <TextInput
                        error={form.errors.getMessage('toelichting')}
                        name="toelichting"
                        onBlur={form.handleBlur}
                        onChange={form.handleChange}
                        placeholder="Een toelichting, opmerkingen of vragen..."
                        value={form.values.toelichting ?? ''}
                        variant="outline"
                    />
                </FormGroup>
            )}
        </>
    );
}

function hasToConvertOldToNewScreeningStandard(customControlData: ITfoDiagnosticReportCustomControlData) {
    const hasOldSreeningStandardValues =
        !!customControlData.bevindingen_npdr_od || !!customControlData.bevindingen_npdr_os;

    const isMissingNewScreeningStandardValues =
        !customControlData.retinopathie_od || !customControlData.retinopathie_os;

    return hasOldSreeningStandardValues && isMissingNewScreeningStandardValues;
}

export function tfoV1DiagnosticReportInitializeCustomControlData() {
    return {
        andere_bevindingen_od: null,
        andere_bevindingen_od_text: null,
        andere_bevindingen_os: null,
        andere_bevindingen_os_text: null,
        geadviseerd_beleid: null,
        geadviseerd_beleid_anders: null,
        maculopathie_od: null,
        maculopathie_os: null,
        retinopathie_od: null,
        retinopathie_os: null,
        toelichting: null,
        urgentie: null,
    };
}

export function RetinopathieAndMaculopathieFields(props: Readonly<{ skipStandardInfo?: boolean }>) {
    const maculopathieOptions = [Maculopathie.m0, Maculopathie.m1, Maculopathie.nietTeBeoordelen];
    const form = useFormContext<ITfoDiagnosticReportCustomControlData>();

    return (
        <>
            {!props.skipStandardInfo && <NewStandardInfo />}

            {[form.values.maculopathie_od, form.values.maculopathie_os].includes(Maculopathie.m1) && (
                <Alert appearance="warning">M1 gaat altijd gepaard met R1, R2 of R3</Alert>
            )}
            <FormGroup
                isRequired={true}
                label="Retinopathie"
                layout="horizontal"
                sx={{ marginTop: '1em' }}
            >
                <Box {...boxProps}>
                    <Col>
                        <h4>OD</h4>
                        <RadioGroup
                            error={form.errors.getMessage('retinopathie_od')}
                            name="retinopathie_od"
                            onBlur={form.handleBlur}
                            onChange={async (event) => {
                                form.handleChange([
                                    {
                                        name: 'retinopathie_od',
                                        value: event.target.value,
                                    },
                                    {
                                        name: 'geadviseerd_beleid',
                                        value: null,
                                    },
                                    {
                                        name: 'geadviseerd_beleid_anders',
                                        value: null,
                                    },
                                ]);
                            }}
                            value={form.values.retinopathie_od ?? ''}
                        >
                            {(form.values.maculopathie_od === Maculopathie.m1
                                ? retinopathieOptionsForMaculopathieM1
                                : retinopathieOptions
                            ).map((option) => (
                                <Radio
                                    key={option}
                                    label={
                                        <StyledRethinopathieMaculopathieItem>
                                            {option}
                                        </StyledRethinopathieMaculopathieItem>
                                    }
                                    value={option}
                                />
                            ))}
                        </RadioGroup>
                    </Col>
                    <Col {...alignLeftElement}>
                        <h4>OS</h4>
                        <RadioGroup
                            error={form.errors.getMessage('retinopathie_os')}
                            name="retinopathie_os"
                            onBlur={form.handleBlur}
                            onChange={(value) => {
                                form.handleChange([
                                    {
                                        name: 'retinopathie_os',
                                        value: value.target.value,
                                    },
                                    {
                                        name: 'geadviseerd_beleid',
                                        value: null,
                                    },
                                    {
                                        name: 'geadviseerd_beleid_anders',
                                        value: null,
                                    },
                                ]);
                            }}
                            value={form.values.retinopathie_os ?? ''}
                        >
                            {(form.values.maculopathie_os === Maculopathie.m1
                                ? retinopathieOptionsForMaculopathieM1
                                : retinopathieOptions
                            ).map((option) => (
                                <Radio
                                    key={option}
                                    label={
                                        <StyledRethinopathieMaculopathieItem>
                                            {option}
                                        </StyledRethinopathieMaculopathieItem>
                                    }
                                    value={option}
                                />
                            ))}
                        </RadioGroup>
                    </Col>
                </Box>
            </FormGroup>
            <FormGroup
                isRequired={true}
                label="Maculopathie"
                layout="horizontal"
            >
                <Box {...boxProps}>
                    <Col>
                        <RadioGroup
                            error={form.errors.getMessage('maculopathie_od')}
                            name="maculopathie_od"
                            onBlur={form.handleBlur}
                            onChange={(value) =>
                                handleMaculopathieChange(form, value.target.value as Maculopathie, 'od')
                            }
                            value={form.values.maculopathie_od ?? ''}
                        >
                            {maculopathieOptions.map((option) => (
                                <Radio
                                    key={option}
                                    label={
                                        <StyledRethinopathieMaculopathieItem>
                                            {option}
                                        </StyledRethinopathieMaculopathieItem>
                                    }
                                    value={option}
                                />
                            ))}
                        </RadioGroup>
                    </Col>
                    <Col {...alignLeftElement}>
                        <RadioGroup
                            error={form.errors.getMessage('maculopathie_os')}
                            name="maculopathie_os"
                            onBlur={form.handleBlur}
                            onChange={(value) =>
                                handleMaculopathieChange(form, value.target.value as Maculopathie, 'os')
                            }
                            value={form.values.maculopathie_os ?? ''}
                        >
                            {maculopathieOptions.map((option) => (
                                <Radio
                                    key={option}
                                    label={
                                        <StyledRethinopathieMaculopathieItem>
                                            {option}
                                        </StyledRethinopathieMaculopathieItem>
                                    }
                                    value={option}
                                />
                            ))}
                        </RadioGroup>
                    </Col>
                </Box>
            </FormGroup>

            <AndereBevindingenFields />
        </>
    );
}

function handleMaculopathieChange(
    form: FormResult<ITfoDiagnosticReportCustomControlData>,
    maculopathie: Maculopathie | null,
    eye: 'od' | 'os'
) {
    const maculopathieKey = `maculopathie_${eye}` as const;
    const retinopathieKey = `retinopathie_${eye}` as const;
    const changes: {
        name: keyof ITfoDiagnosticReportCustomControlData;
        value: Maculopathie | null;
    }[] = [
        { name: maculopathieKey, value: maculopathie },
        { name: 'geadviseerd_beleid', value: null },
        { name: 'geadviseerd_beleid_anders', value: null },
    ];

    if (
        maculopathie === Maculopathie.m1 &&
        form.values[`retinopathie_${eye}` as keyof ITfoDiagnosticReportCustomControlData] === Retinopathie.r0
    ) {
        changes.push({ name: retinopathieKey, value: null });
    }

    form.handleChange(changes);
}

export function PolicyAndUrgencyAddEditFields() {
    return (
        <FundusscreeningPolicyAndUrgencyAddEditFields
            advisedPolicies={advisedPolicies}
            requiredPolicy={true}
        />
    );
}
